import { RootState } from "@/app/store.ts";
import {
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import {
    JSONB_KEYS,
    objectKeysToCamel,
    objectKeysToSnake,
} from "@/util/case.ts";
import {
    getSelectedWorkspace,
    workspaceUnavailable,
} from "@/features/Workspace/Workspace.slice.ts";
import { fetchAuthSession } from "aws-amplify/auth";
import { getCookie } from "@/util/cookie.ts";
import { GA_SESSION_ID_COOKIE } from "@/app/auth/gaSession.ts";
import { toast } from "react-hot-toast";
import {
    ApiError,
    apiErrorFromException,
    normalizeApiError,
} from "@/services/apis/apiError.ts";

const baseQueryFactory = (baseUrl: string) =>
    fetchBaseQuery({
        baseUrl,
        prepareHeaders: async (headers, { getState, ...args }) => {
            const state = getState() as RootState;
            const { tokens } = await fetchAuthSession();

            if (!tokens?.accessToken) {
                throw new Error("No access token");
            }

            headers.set("authorization", `Bearer ${tokens.accessToken}`);

            // The admin store has no workspace slice: the admin has no
            // workspace scope, so its requests never carry this header.
            const workspaceId =
                "workspace" in state
                    ? getSelectedWorkspace(state)?.id
                    : undefined;
            if (workspaceId) {
                headers.set("Spectacle-Workspace-Id", workspaceId);
            }

            if (
                typeof args.arg === "object" &&
                "managedOrganizationId" in args.arg &&
                !!args.arg.managedOrganizationId
            ) {
                headers.set(
                    "Spectacle-Managed-Organization-Id",
                    // @ts-expect-error its here alright
                    args.arg.managedOrganizationId,
                );
            }

            // Where a new account came from. Only POST /auth/session reads
            // these, and it is the first request after a sign-in; the cookie
            // is cleared once that succeeds (ServerSession), so a failed
            // first attempt does not lose them.
            if (getCookie("potentialSignUp")) {
                const tapfiliateVisitorId = getCookie("tap_vid");
                if (tapfiliateVisitorId) {
                    headers.set(
                        "Spectacle-Tapfiliate-Vid",
                        tapfiliateVisitorId,
                    );
                }

                const isUtmScout = getCookie("isUtmScout");
                if (isUtmScout) {
                    headers.set("Spectacle-Is-Utm-Scout", "1");
                }

                // The Icp audit link the sign-up started from
                // (app/audit/AuditLandingScreen.tsx).
                const auditProspect = getCookie("auditProspect");
                if (auditProspect) {
                    headers.set("Spectacle-Audit-Prospect", auditProspect);
                }

                const gaClientId = getCookie("_ga");
                if (gaClientId) {
                    const parts = gaClientId.split(".");
                    if (parts.length > 2) {
                        headers.set(
                            "Spectacle-Ga-Client-Id",
                            `${parts[2]}.${parts[3]}`,
                        );
                    }
                }

                const gaSessionId = getCookie(GA_SESSION_ID_COOKIE);
                if (gaSessionId) {
                    headers.set("Spectacle-Ga-Session-Id", gaSessionId);
                }

                const vwoUuid = getCookie("_vwo_uuid");
                if (vwoUuid) {
                    headers.set("Spectacle-Vwo-Uuid", vwoUuid);
                }
            }

            return headers;
        },
        credentials: "include",
    });

/**
 * Per-endpoint opt-outs from the global error toast.
 *
 * The toast is the default when callers do not display errors themselves. Set
 * `errorToast: false` when all callers render them; use `expectedStatuses` when only
 * particular responses should be quiet. A 404 from a
 * "does this workspace have one?" read is the state the screen is built to render, and
 * toasting it means an error pops on arrival and again after the action that made it true.
 */
export interface BaseQueryExtraOptions {
    /** Set false only when every caller displays errors itself. Error state is preserved. */
    errorToast?: boolean;
    /** Statuses to return without toasting. The caller renders them itself. */
    expectedStatuses?: number[];
}

export function getBaseQuery(
    baseUrl: string,
): BaseQueryFn<FetchArgs, unknown, ApiError, BaseQueryExtraOptions> {
    const baseQuery = baseQueryFactory(baseUrl);
    return async (args, api, extraOptions) => {
        if (args.body) {
            args.body = objectKeysToSnake(
                args.body as Record<string, unknown>,
                JSONB_KEYS,
            );
        }

        let result: Awaited<ReturnType<typeof baseQuery>>;
        try {
            result = await baseQuery(args, api, extraOptions);
        } catch (error) {
            result = { error: apiErrorFromException(error) };
        }

        if (result.error) {
            const error = normalizeApiError(result.error);

            if (
                error.status === 404 &&
                error.code === "workspace_unavailable"
            ) {
                const workspaceId = result.meta?.request.headers.get(
                    "Spectacle-Workspace-Id",
                );
                if (workspaceId) {
                    api.dispatch(workspaceUnavailable(workspaceId));
                    // Recovery is handled by the store. Do not replay this
                    // request: a mutation must never move to another workspace.
                    return { ...result, error };
                }
            }

            const expected =
                typeof result.error.status === "number" &&
                (extraOptions?.expectedStatuses?.includes(
                    result.error.status,
                ) ??
                    false);

            // Integration activation failures are surfaced inline in the relevant
            // panels via Notice, not as toasts — the toast is global and would
            // duplicate the contextual feedback the user is already getting.
            if (
                extraOptions?.errorToast !== false &&
                error.code !== "integration_error" &&
                !expected
            ) {
                const message =
                    error.message ||
                    "An unknown error occurred. Please try again. If the problem persists, please contact support";
                toast.error(message);
            }

            return { ...result, error };
        }

        result.data = objectKeysToCamel(result.data, [
            "meta_data",
            "settings",
            "headers", // Automation debugger
            "evidence", // Check results — keys are backend-defined display data
        ]);

        return result;
    };
}
